import { AxiosError, AxiosResponse } from "axios";
import { useQuery, QueryObserverResult, UseQueryOptions } from "react-query";

import {
  OperationResponseV4,
  OperationV4,
} from "../../../../models/operations";
import {
  FetcherQueryKey,
  computeIsQueryEnabled,
  retryUnless404,
} from "../../../../services/query";

import { useMaybeUserId } from "../../userId";

import {
  useMaybeCurrentOperationId,
  useClearCurrentOperationId,
} from "../currentOperationId";
import { useInvalidatePreOperations } from "../preOperations";
import { useSetCurrentOperationDataGlobalActivityAndStatus } from "../currentOperation/currentOperation.globalStatus";

import {
  OperationFetcherParams,
  OperationQueryKey,
  operationV4QueryKeyPrefix,
  computeOperationQueryKey,
  operationFetcher,
  computeResultOfUseQuery,
  onSuccess,
  onError,
  isDataEqual,
  useHandleLoadingStatus,
  useCleanupMemoization,
} from "./operation.query.helpers";
import { useMaybeFabricFeatureFlag } from "../../featureFlags";

export type { OperationFetcherParams, OperationQueryKey };
export { operationV4QueryKeyPrefix, computeOperationQueryKey };

// #######################
// ## useOperationQuery ##
// #######################

export type ParamsOfUseOperationQuery = {
  operationId: string | undefined;
  watch?: UseQueryOptions["notifyOnChangeProps"];
};
export type ResultOfUseOperationQuery = QueryObserverResult<
  OperationV4,
  AxiosError
>;
export type UseOperationQuery = (
  params: ParamsOfUseOperationQuery,
) => ResultOfUseOperationQuery;

/**
 * The returned value of this hook will contain the React Query result
 * of the fetching of an operation.
 *
 * This hook is only meant for use by the hooks in `./operation.ts`.
 * Do not use this hook outside these hooks as this could couple us to React Query.
 */
export const useOperationQuery: UseOperationQuery = ({
  operationId,
  watch,
}) => {
  const userId = useMaybeUserId();
  const currentOperationId = useMaybeCurrentOperationId();
  const clearCurrentOperationId = useClearCurrentOperationId();
  const invalidatePreOperations = useInvalidatePreOperations();

  const setCurrentOperationDataGlobalActivityAndStatus =
    useSetCurrentOperationDataGlobalActivityAndStatus();

  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const params = { userId, operationId, catalogFlag };
  const queryKey = computeOperationQueryKey(params);
  const resultOfUseQuery = useQuery<
    AxiosResponse<OperationResponseV4>,
    AxiosError,
    AxiosResponse<OperationResponseV4>,
    FetcherQueryKey<OperationFetcherParams>
  >(queryKey, operationFetcher, {
    enabled: computeIsQueryEnabled(params),
    notifyOnChangeProps: watch || "tracked",
    onSuccess: () =>
      onSuccess({
        currentOperationId,
        operationId,
        setCurrentOperationDataGlobalActivityAndStatus,
      }),
    retry: retryUnless404,
    onError: (error) =>
      onError({
        error,
        operationId,
        currentOperationId,
        clearCurrentOperationId,
        invalidatePreOperations,
        setCurrentOperationDataGlobalActivityAndStatus,
      }),
    isDataEqual,
  });

  useHandleLoadingStatus({
    currentOperationId,
    operationId,
    resultOfUseQuery,
    setCurrentOperationDataGlobalActivityAndStatus,
  });

  useCleanupMemoization(queryKey);

  return computeResultOfUseQuery(resultOfUseQuery);
};
